import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import ThemeToggle from "@/components/ThemeToggle";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  // { to: "/case-studies", label: "Work" },
  { to: "/about", label: "About" },
  // { to: "/careers", label: "Careers" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      className={`sticky top-0 z-50 border-b transition-colors duration-300 ${
        scrolled
          ? "bg-[#05060F]/85 backdrop-blur-xl border-[#1E2BFF]/25"
          : "bg-transparent border-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
        <div className="flex items-center justify-between h-20 gap-4">
          <Link
            to="/"
            className="flex items-center min-w-0 shrink"
            data-testid="nav-logo-link"
          >
            <BrandLogo className="h-10 sm:h-14 max-w-[130px] sm:max-w-[200px] shrink object-contain" />
          </Link>

          <nav className="hidden lg:flex items-center gap-10">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `font-mono text-[11px] tracking-[0.28em] uppercase link-underline transition-colors ${
                    isActive ? "text-[#00F0FF]" : "text-white/80 hover:text-white"
                  }`
                }
                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3 shrink-0">
            <div className="hidden lg:block">
              <ThemeToggle />
            </div>
            <div className="hidden lg:block">
              <Link
                to="/contact"
                className="btn-primary h-11 items-center justify-center whitespace-nowrap !px-4"
                data-testid="nav-contact-cta"
              >
                <span className="pulse-dot" />
                Initiate Contact
              </Link>
            </div>
            <button
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden w-11 h-11 shrink-0 border border-[#1E2BFF]/40 text-white flex items-center justify-center"
              aria-label="Toggle menu"
              data-testid="nav-mobile-toggle"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        {open && (
          <div
            className="lg:hidden pb-6 pt-2 border-t border-[#1E2BFF]/20"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-1">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    `py-3 px-2 font-mono text-[12px] tracking-[0.28em] uppercase ${
                      isActive ? "text-[#00F0FF]" : "text-white/80"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                className="btn-primary mt-4"
                data-testid="nav-mobile-contact-cta"
              >
                <span className="pulse-dot" /> Initiate Contact
              </Link>
              <div className="mt-6 px-2 flex items-center justify-between border-t border-[#1E2BFF]/20 pt-4">
                <span className="font-mono text-[11px] tracking-[0.28em] uppercase text-white/80">Theme</span>
                <ThemeToggle />
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
